/**
 * Pure document mutations. Every op returns a new document, or the *same*
 * document (by reference) when the change is invalid or has no effect, so
 * callers can cheaply detect no-ops. Ops never break the invariants checked by
 * `findInvariantViolations`.
 */
import { findCategory, findKink, findRating, unsetRating } from './access';
import { normalizeColor, normalizeComment, normalizeName, normalizeTitle } from './text';
import type { Category, Id, Kink, KinklistDoc, Rating, Role } from './types';
import { LIMITS } from './types';

type Doc = KinklistDoc;

export interface NewRole {
  id: Id;
  name: string;
}

// ---------------------------------------------------------------- helpers

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function insertAt<T>(list: readonly T[], index: number, item: T): T[] {
  const at = clamp(Math.trunc(index), 0, list.length);
  return [...list.slice(0, at), item, ...list.slice(at)];
}

function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list];
  next.splice(to, 0, ...next.splice(from, 1));
  return next;
}

/** Like `Array#map`, but returns `list` itself when every element maps to itself. */
function mapSame<T>(list: readonly T[], update: (item: T) => T): readonly T[] {
  const next = list.map(update);
  return next.every((item, i) => item === list[i]) ? list : next;
}

function usedIds(doc: Doc): Set<Id> {
  const ids = new Set<Id>(doc.ratings.map((r) => r.id));
  for (const category of doc.categories) {
    ids.add(category.id);
    for (const role of category.roles) ids.add(role.id);
    for (const kink of category.kinks) ids.add(kink.id);
  }
  return ids;
}

function withCategories(doc: Doc, categories: readonly Category[]): Doc {
  return categories === doc.categories ? doc : { ...doc, categories: [...categories] };
}

function updateCategory(doc: Doc, categoryId: Id, update: (c: Category) => Category): Doc {
  return withCategories(
    doc,
    mapSame(doc.categories, (category) =>
      category.id === categoryId ? update(category) : category,
    ),
  );
}

function updateKink(doc: Doc, kinkId: Id, update: (k: Kink, c: Category) => Kink): Doc {
  const location = findKink(doc, kinkId);
  if (!location) return doc;
  const next = update(location.kink, location.category);
  if (next === location.kink) return doc;
  return updateCategory(doc, location.category.id, (category) => ({
    ...category,
    kinks: category.kinks.map((kink) => (kink.id === kinkId ? next : kink)),
  }));
}

/** Rewrites every selection through `map`; returns the same doc if nothing changed. */
function mapSelections(doc: Doc, map: (ratingId: Id) => Id): Doc {
  const mapKink = (kink: Kink): Kink => {
    const entries = Object.entries(kink.ratings);
    const mapped = entries.map(([roleId, ratingId]) => [roleId, map(ratingId)] as const);
    const same = mapped.every(([, ratingId], i) => ratingId === entries[i]?.[1]);
    return same ? kink : { ...kink, ratings: Object.fromEntries(mapped) };
  };
  return withCategories(
    doc,
    mapSame(doc.categories, (category) => {
      const kinks = mapSame(category.kinks, mapKink);
      return kinks === category.kinks ? category : { ...category, kinks: [...kinks] };
    }),
  );
}

function normalizeRoles(roles: readonly NewRole[]): Role[] | null {
  if (roles.length < LIMITS.minRoles || roles.length > LIMITS.maxRoles) return null;
  if (new Set(roles.map((r) => r.id)).size !== roles.length) return null;
  const normalized: Role[] = [];
  for (const role of roles) {
    const name = normalizeName(role.name);
    if (name === null) return null;
    normalized.push({ id: role.id, name });
  }
  return normalized;
}

// ---------------------------------------------------------------- title

export function setTitle(doc: Doc, title: string): Doc {
  const next = normalizeTitle(title);
  return next === doc.title ? doc : { ...doc, title: next };
}

// ---------------------------------------------------------------- selections & comments

export function setRating(doc: Doc, kinkId: Id, roleId: Id, ratingId: Id): Doc {
  if (!findRating(doc, ratingId)) return doc;
  return updateKink(doc, kinkId, (kink, category) => {
    if (!category.roles.some((role) => role.id === roleId)) return kink;
    if (kink.ratings[roleId] === ratingId) return kink;
    return { ...kink, ratings: { ...kink.ratings, [roleId]: ratingId } };
  });
}

/** Sets (or, when blank, removes) a kink's comment. */
export function setComment(doc: Doc, kinkId: Id, comment: string): Doc {
  const value = normalizeComment(comment);
  return updateKink(doc, kinkId, (kink) => {
    if (kink.comment === value) return kink;
    const next = { ...kink };
    if (value === undefined) delete next.comment;
    else next.comment = value;
    return next;
  });
}

/** Clears every rating (back to the unset rating) and every comment; the list itself stays. */
export function clearSelections(doc: Doc): Doc {
  const unsetId = unsetRating(doc.ratings).id;
  const cleared = mapSelections(doc, () => unsetId);
  return withCategories(
    cleared,
    mapSame(cleared.categories, (category) => {
      const kinks = mapSame(category.kinks, (kink) => {
        if (kink.comment === undefined) return kink;
        const next = { ...kink };
        delete next.comment;
        return next;
      });
      return kinks === category.kinks ? category : { ...category, kinks: [...kinks] };
    }),
  );
}

// ---------------------------------------------------------------- categories

export function addCategory(
  doc: Doc,
  input: { id: Id; name: string; roles: readonly NewRole[]; index?: number },
): Doc {
  const name = normalizeName(input.name);
  const roles = normalizeRoles(input.roles);
  if (name === null || roles === null) return doc;
  const ids = usedIds(doc);
  if (ids.has(input.id) || roles.some((role) => ids.has(role.id) || role.id === input.id)) {
    return doc;
  }
  const category: Category = { id: input.id, name, roles, kinks: [] };
  return {
    ...doc,
    categories: insertAt(doc.categories, input.index ?? doc.categories.length, category),
  };
}

export function renameCategory(doc: Doc, categoryId: Id, name: string): Doc {
  const next = normalizeName(name);
  if (next === null) return doc;
  return updateCategory(doc, categoryId, (category) =>
    category.name === next ? category : { ...category, name: next },
  );
}

export function removeCategory(doc: Doc, categoryId: Id): Doc {
  const categories = doc.categories.filter((category) => category.id !== categoryId);
  return categories.length === doc.categories.length ? doc : { ...doc, categories };
}

export function moveCategory(doc: Doc, categoryId: Id, toIndex: number): Doc {
  const from = doc.categories.findIndex((category) => category.id === categoryId);
  if (from === -1) return doc;
  const to = clamp(Math.trunc(toIndex), 0, doc.categories.length - 1);
  return from === to ? doc : { ...doc, categories: moveItem(doc.categories, from, to) };
}

export function setCollapsed(doc: Doc, categoryId: Id, collapsed: boolean): Doc {
  return updateCategory(doc, categoryId, (category) => {
    if ((category.collapsed ?? false) === collapsed) return category;
    const next = { ...category };
    if (collapsed) next.collapsed = true;
    else delete next.collapsed;
    return next;
  });
}

export function setAllCollapsed(doc: Doc, collapsed: boolean): Doc {
  return doc.categories.reduce(
    (current, category) => setCollapsed(current, category.id, collapsed),
    doc,
  );
}

/**
 * Replaces a category's roles (rename, add, remove, reorder in one go).
 * Selections of kept roles survive; new roles start unset.
 */
export function setCategoryRoles(doc: Doc, categoryId: Id, roles: readonly NewRole[]): Doc {
  const category = findCategory(doc, categoryId);
  const normalized = normalizeRoles(roles);
  if (!category || normalized === null) return doc;
  const own = new Set(category.roles.map((role) => role.id));
  const ids = usedIds(doc);
  if (normalized.some((role) => !own.has(role.id) && ids.has(role.id))) return doc;
  const key = (list: readonly Role[]) => JSON.stringify(list.map((role) => [role.id, role.name]));
  if (key(normalized) === key(category.roles)) return doc;
  const unsetId = unsetRating(doc.ratings).id;
  return updateCategory(doc, categoryId, (c) => ({
    ...c,
    roles: normalized,
    kinks: c.kinks.map((kink) => ({
      ...kink,
      ratings: Object.fromEntries(
        normalized.map((role) => [role.id, kink.ratings[role.id] ?? unsetId]),
      ),
    })),
  }));
}

// ---------------------------------------------------------------- kinks

export function addKink(
  doc: Doc,
  categoryId: Id,
  input: { id: Id; name: string; index?: number },
): Doc {
  const name = normalizeName(input.name);
  if (name === null || usedIds(doc).has(input.id)) return doc;
  const unsetId = unsetRating(doc.ratings).id;
  return updateCategory(doc, categoryId, (category) => {
    const kink: Kink = {
      id: input.id,
      name,
      ratings: Object.fromEntries(category.roles.map((role) => [role.id, unsetId])),
    };
    return {
      ...category,
      kinks: insertAt(category.kinks, input.index ?? category.kinks.length, kink),
    };
  });
}

/** Renames a kink. A blank name is rejected (v2 silently deleted the kink). */
export function renameKink(doc: Doc, kinkId: Id, name: string): Doc {
  const next = normalizeName(name);
  if (next === null) return doc;
  return updateKink(doc, kinkId, (kink) => (kink.name === next ? kink : { ...kink, name: next }));
}

export function removeKink(doc: Doc, kinkId: Id): Doc {
  const location = findKink(doc, kinkId);
  if (!location) return doc;
  return updateCategory(doc, location.category.id, (category) => ({
    ...category,
    kinks: category.kinks.filter((kink) => kink.id !== kinkId),
  }));
}

/** Moves a kink within its category. */
export function moveKink(doc: Doc, kinkId: Id, toIndex: number): Doc {
  const location = findKink(doc, kinkId);
  if (!location) return doc;
  const { category, kinkIndex } = location;
  const to = clamp(Math.trunc(toIndex), 0, category.kinks.length - 1);
  if (to === kinkIndex) return doc;
  return updateCategory(doc, category.id, (c) => ({
    ...c,
    kinks: moveItem(c.kinks, kinkIndex, to),
  }));
}

// ---------------------------------------------------------------- ratings

/** Adds a rating. Index 0 is reserved for the "unset" rating, so inserts land at ≥ 1. */
export function addRating(
  doc: Doc,
  input: { id: Id; name: string; color: string; index?: number },
): Doc {
  const name = normalizeName(input.name);
  const color = normalizeColor(input.color);
  if (name === null || color === null || usedIds(doc).has(input.id)) return doc;
  const index = Math.max(1, input.index ?? doc.ratings.length);
  return { ...doc, ratings: insertAt(doc.ratings, index, { id: input.id, name, color }) };
}

function updateRating(doc: Doc, ratingId: Id, patch: Partial<Omit<Rating, 'id'>>): Doc {
  const ratings = mapSame(doc.ratings, (rating) => {
    if (rating.id !== ratingId) return rating;
    const next = { ...rating, ...patch };
    return next.name === rating.name && next.color === rating.color ? rating : next;
  });
  return ratings === doc.ratings ? doc : { ...doc, ratings: [...ratings] };
}

/** Renames a rating. Selections reference ids, so they are unaffected (v2 reset them). */
export function renameRating(doc: Doc, ratingId: Id, name: string): Doc {
  const next = normalizeName(name);
  return next === null ? doc : updateRating(doc, ratingId, { name: next });
}

export function recolorRating(doc: Doc, ratingId: Id, color: string): Doc {
  const next = normalizeColor(color);
  return next === null ? doc : updateRating(doc, ratingId, { color: next });
}

/** Whether `removeRating` would accept this rating (not the unset rating, ≥ 2 remain). */
export function canRemoveRating(doc: Doc, ratingId: Id): boolean {
  const index = doc.ratings.findIndex((rating) => rating.id === ratingId);
  return index > 0 && doc.ratings.length > LIMITS.minRatings;
}

/** Removes a rating; its selections fall back to the unset rating. */
export function removeRating(doc: Doc, ratingId: Id): Doc {
  if (!canRemoveRating(doc, ratingId)) return doc;
  const unsetId = unsetRating(doc.ratings).id;
  const reassigned = mapSelections(doc, (id) => (id === ratingId ? unsetId : id));
  return { ...reassigned, ratings: doc.ratings.filter((rating) => rating.id !== ratingId) };
}

/** Reorders a rating among indices ≥ 1; the unset rating stays first. */
export function moveRating(doc: Doc, ratingId: Id, toIndex: number): Doc {
  const from = doc.ratings.findIndex((rating) => rating.id === ratingId);
  if (from < 1) return doc;
  const to = clamp(Math.trunc(toIndex), 1, doc.ratings.length - 1);
  return from === to ? doc : { ...doc, ratings: moveItem(doc.ratings, from, to) };
}

/**
 * Replaces the legend with `ratings` (e.g. the defaults). Selections move to
 * the replacement with the same name (case-insensitive), else to the new unset
 * rating.
 */
export function replaceRatings(doc: Doc, replacement: readonly Rating[]): Doc {
  const ratings: Rating[] = [];
  for (const rating of replacement) {
    const name = normalizeName(rating.name);
    const color = normalizeColor(rating.color);
    if (name === null || color === null) return doc;
    ratings.push({ id: rating.id, name, color });
  }
  if (ratings.length < LIMITS.minRatings) return doc;
  const ids = new Set(ratings.map((rating) => rating.id));
  if (ids.size !== ratings.length) return doc;
  const otherIds = usedIds({ ...doc, ratings: [] });
  if ([...ids].some((id) => otherIds.has(id))) return doc;
  const byName = new Map<string, Id>();
  for (const rating of [...ratings].reverse()) byName.set(rating.name.toLowerCase(), rating.id);
  const oldNames = new Map(doc.ratings.map((rating) => [rating.id, rating.name.toLowerCase()]));
  const unsetId = unsetRating(ratings).id;
  const reassigned = mapSelections(doc, (id) => byName.get(oldNames.get(id) ?? '') ?? unsetId);
  return { ...reassigned, ratings };
}

/**
 * Replaces the legend with an edited version of it (the ratings editor's
 * draft): kept ids keep their selections, removed ratings' selections fall
 * back to the unset rating, new ids are added. The unset rating must stay
 * first and be kept.
 */
export function setRatings(doc: Doc, draft: readonly Rating[]): Doc {
  const unsetId = unsetRating(doc.ratings).id;
  if (draft.length < LIMITS.minRatings || draft[0]?.id !== unsetId) return doc;
  const ratings: Rating[] = [];
  for (const rating of draft) {
    const name = normalizeName(rating.name);
    const color = normalizeColor(rating.color);
    if (name === null || color === null) return doc;
    ratings.push({ id: rating.id, name, color });
  }
  const ids = new Set(ratings.map((rating) => rating.id));
  if (ids.size !== ratings.length) return doc;
  const otherIds = usedIds({ ...doc, ratings: [] });
  if ([...ids].some((id) => otherIds.has(id))) return doc;
  const same =
    ratings.length === doc.ratings.length &&
    ratings.every((rating, i) => {
      const old = doc.ratings[i];
      return old?.id === rating.id && old.name === rating.name && old.color === rating.color;
    });
  if (same) return doc;
  const reassigned = mapSelections(doc, (id) => (ids.has(id) ? id : unsetId));
  return { ...reassigned, ratings };
}
